import axios from "axios";
import { authStore } from "./authStore";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/$/, "");
export const API_BASE = BACKEND_URL.endsWith("/api") ? BACKEND_URL : `${BACKEND_URL}/api`;

/** Media refs come back as backend-relative paths ("/api/media/…"). In dev the
 * storefront runs on its own port, so a relative src would hit the CRA server
 * and 404 — every media URL must be anchored to the backend origin. */
export function mediaUrl(path) {
  if (!path) return path;
  if (/^(?:https?:\/\/|data:|blob:)/i.test(path)) return path;
  // Webpack/Vite asset imports already belong to the storefront origin. Only
  // backend-relative media paths must be anchored to the API origin.
  if (path.startsWith("/static/")) return path;
  return `${BACKEND_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

/** Yalnız backend-in öz media şəkilləri üçün kiçildilmiş variant (?w=…).
 * Kənar hostlar və video yolları olduğu kimi qalır — resize ladder onlara
 * şamil deyil. Plitə üçün orijinal 10MB şəkli çəkməyin qarşısını alır. */
export function sizedMediaUrl(path, width) {
  const url = mediaUrl(path);
  if (!url || !width) return url;
  if (!url.includes("/api/media/")) return url;
  return `${url}${url.includes("?") ? "&" : "?"}w=${width}`;
}

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((cfg) => {
  const t = authStore.getAccessToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

let refreshInFlight = null;

/**
 * Mint a fresh access token from the stored refresh token.
 *
 * Exported because the 401 interceptor is not the only caller: on a cold start
 * the access token is gone (it lives in sessionStorage) while the refresh token
 * survived in localStorage, and there is no 401 to react to — nothing has been
 * requested yet. `AuthProvider.load()` calls this directly so a returning
 * customer is signed in before the first render instead of being bounced to
 * the login page. De-duplicated through the same `refreshInFlight` promise.
 */
export function ensureAccessToken() {
  if (authStore.getAccessToken()) return Promise.resolve(authStore.getAccessToken());
  if (!authStore.getRefreshToken()) return Promise.resolve(null);
  refreshInFlight = refreshInFlight || refreshAccessToken().finally(() => { refreshInFlight = null; });
  return refreshInFlight.catch(() => null);
}

async function refreshAccessToken() {
  const refreshToken = authStore.getRefreshToken();
  if (!refreshToken) return null;
  try {
    const response = await axios.post(`${API_BASE}/auth/refresh`, {
      refresh_token: refreshToken,
    });
    authStore.setTokens(response.data.access_token, response.data.refresh_token);
    return response.data.access_token;
  } catch (error) {
    authStore.clear();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("atelier:auth-expired"));
    }
    throw error;
  }
}


/**
 * Flatten an API error body into something React can render.
 *
 * FastAPI answers a validation failure (422) with `detail` as a LIST of objects
 * — `{type, loc, msg, input, ctx, url}` — while every other error answers with
 * a string. Thirty-one call sites in this app put `detail` straight into state
 * and hand it to JSX, so one 422 throws "Objects are not valid as a React
 * child", React unmounts the tree, and the page goes white.
 *
 * Checkout is already safe (`lib/apiErrors.js` guards the type), but nothing
 * else is. Normalising here fixes every one of them at once and makes the next
 * call site safe by default.
 */
function detailToText(detail) {
  if (detail == null) return undefined;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const lines = detail.map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        // `loc` is the path to the offending field — the only part of FastAPI's
        // object a shopper or an operator can act on.
        const loc = Array.isArray(item.loc)
          ? item.loc.filter((part) => part !== "body").join(" → ")
          : "";
        const msg = typeof item.msg === "string" ? item.msg : JSON.stringify(item);
        return loc ? `${loc}: ${msg}` : msg;
      }
      return String(item);
    }).filter(Boolean);
    return lines.length ? lines.join("; ") : undefined;
  }
  if (typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    try { return JSON.stringify(detail); } catch { return String(detail); }
  }
  return String(detail);
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const path = String(original?.url || "");
    const isAuthAction = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout", "/auth/otp/request", "/auth/otp/verify"]
      .some((route) => path.includes(route));

    if (
      error.response?.status === 401
      && original
      && !original._atelierRetried
      && !isAuthAction
      && authStore.getRefreshToken()
    ) {
      original._atelierRetried = true;
      refreshInFlight = refreshInFlight || refreshAccessToken();
      try {
        const accessToken = await refreshInFlight;
        if (accessToken) {
          original.headers = { ...original.headers, Authorization: `Bearer ${accessToken}` };
          return api.request(original);
        }
      } finally {
        refreshInFlight = null;
      }
    }

    // Rewrite the body in place so every consumer — including the thirty-one
    // that read `detail` directly — receives a string.
    const body = error.response?.data;
    if (body && typeof body === "object" && body.detail != null && typeof body.detail !== "string") {
      body.detail = detailToText(body.detail) ?? "Sorğu qəbul edilmədi.";
    }
    return Promise.reject(error);
  },
);

export const routes = {
  categories: () => `/categories`,
  collections: () => `/collections`,
  collection: (handle) => `/collections/${handle}`,
  products: (params) => ({ url: `/products`, params }),
  product: (handle) => `/products/${handle}`,
  journal: () => `/journal`,
  paymentProviders: () => `/payment-providers`,
};
